import React from 'react'
import "../App.css"
import "../css/Transitions.css"
import * as UploadUtil from "../library/UploadUtil"
import { Ad } from "../interface/Ad"
import { Upload, Quality, QualityEnum } from "../interface/Upload"
import Config from '../library/Config'
import UploadController from './UploadController'
import AdController from './AdController'
import GridWrapper from './GridWrapper'
import CalloutController from './CalloutController'
import ReactGA from 'react-ga'

enum DisplayAction {
  UPLOADS = "UPLOADS",
  ADS = "ADS",
  CALLOUTS = "CALLOUTS"
}
type DisplayStepEnum = keyof typeof DisplayAction;

interface DisplayProps {
  ads: Ad[]
  availableUploads: Upload[]
  callouts: Upload[]
  config: Config
  deletedCount: number
  didShowNewGroup: Function
  didDisplayCallouts: Function
}

interface DisplayState {
  action: DisplayStepEnum
  lastUploadIndex: number
  newGroup: Upload[]
  groupSize: number
  cycleCount: number
  cyclesSinceAds: number,
  quality: QualityEnum
  calloutsReady: boolean
  adsReady: boolean
}

export default class DisplayController extends React.Component<DisplayProps, DisplayState> {
  actionTimer?: any

  constructor(props: DisplayProps) {
    super(props)

    let initialGroupSize = props.config.rows * props.config.columns
    let initialUploadGroupData = UploadUtil.getUploadGroupData(0, initialGroupSize, props.availableUploads)

    this.state = {
      action: DisplayAction.UPLOADS,
      lastUploadIndex: initialUploadGroupData.lastIndex,
      newGroup: initialUploadGroupData.uploads,
      groupSize: initialGroupSize,
      cycleCount: 0,
      cyclesSinceAds: 1,
      quality: this.selectQuality(initialGroupSize),
      calloutsReady: false,
      adsReady: false
    }
  }

  componentWillUnmount() {
    clearTimeout(this.actionTimer)
  }

  componentDidUpdate(prevProps: DisplayProps, _prevState: DisplayState) {
    if (this.props.config.rows !== prevProps.config.rows
      || this.props.config.columns !== prevProps.config.columns
      || this.props.deletedCount !== prevProps.deletedCount) {
      this.changeLayout()
    }
    if (JSON.stringify(this.props.callouts) !== JSON.stringify(prevProps.callouts)) {
      this.setState({ calloutsReady: false })
    }
    if (JSON.stringify(this.props.ads) !== JSON.stringify(prevProps.ads)) {
      this.setState({ adsReady: false })
    }
  }

  selectQuality(groupSize: number): QualityEnum {
    let quality = Quality.LOW
    if (groupSize <= 6) {
      quality = Quality.HIGH
    } else if (groupSize < 24) {
      quality = Quality.MEDIUM
    }

    this.props.config.debug && console.log(`Select upload quality: ${quality}`)
    return quality
  }

  changeLayout() {
    let newGroupSize = this.props.config.rows * this.props.config.columns
    this.setState({
      groupSize: newGroupSize,
      quality: this.selectQuality(newGroupSize)
    }, () => this.showNextUploads())
  }

  nextAction() {
    this.props.config.debug && console.log(`DisplayController: nextAction()`)

    let newCycleCount = this.state.cycleCount + 1
    let cyclesSinceAds = this.state.cyclesSinceAds

    let newAction = DisplayAction.UPLOADS
    if (this.props.ads.length > 0
      && this.state.cyclesSinceAds >= this.props.config.cyclesBetweenAds
      && this.state.action !== DisplayAction.ADS
      && this.state.adsReady
    ) {
      newAction = DisplayAction.ADS
      cyclesSinceAds = 0
      ReactGA.event({ category: "Display Action", action: "Ads", value: this.props.ads.length })
    } else if (this.props.callouts.length > 0
      && this.state.action !== DisplayAction.CALLOUTS
      && this.state.calloutsReady
    ) {
      newAction = DisplayAction.CALLOUTS
      ReactGA.event({ category: "Display Action", action: "Callouts", value: this.props.callouts.length })
    } else {
      cyclesSinceAds += 1
      ReactGA.event({ category: "Display Action", action: "Uploads", value: this.state.groupSize })
    }

    this.props.config.debug && console.log(`Perform next action: ${newAction}`)
    this.setState({
      cycleCount: newCycleCount,
      action: newAction,
      cyclesSinceAds: cyclesSinceAds
    }, () => {
      newAction === DisplayAction.UPLOADS && this.showNextUploads()
    })
  }

  showNextUploads() {
    let newStartingIndex = this.state.lastUploadIndex + 1
    let groupData = UploadUtil.getUploadGroupData(newStartingIndex, this.state.groupSize, this.props.availableUploads)

    this.props.config.debug && console.log(`Preparing to show ${groupData.uploads.length} uploads from ${newStartingIndex} to ${groupData.lastIndex}`)

    this.setState({
      newGroup: groupData.uploads,
      lastUploadIndex: groupData.lastIndex
    }, () => {
      this.props.didShowNewGroup()
    })
  }

  handleFinishCallouts() {
    this.nextAction()
    this.actionTimer = setTimeout(this.props.didDisplayCallouts, 1000)
  }

  render() {
    let config = this.props.config
    return (
      <>
        <AdController
          ads={this.props.ads}
          show={this.state.action === DisplayAction.ADS}
          showOneAdPerCycle={config.showOneAdPerCycle}
          defaultAdDuration={config.adDuration * 1000}
          didFullyLoad={() => this.setState({ adsReady: true })}
          didFinishCycle={() => this.nextAction()}
          debug={config.debug}
        />
        <CalloutController
          uploads={this.props.callouts}
          show={this.state.action === DisplayAction.CALLOUTS}
          calloutDuration={5000}
          didFullyLoad={() => this.setState({ calloutsReady: true })}
          didFinishCycle={() => this.handleFinishCallouts()}
          debug={config.debug}
        />
        <GridWrapper
          show={this.state.action === DisplayAction.UPLOADS}
          columns={config.columns}
          margin={config.margin}
        >
          <UploadController
            show={this.state.action === DisplayAction.UPLOADS}
            uploads={this.state.newGroup}
            transitionName={config.transition}
            photoDelay={config.photoDelay * 1000}
            cycleStartDelay={config.cycleStartDelay * 1000}
            cycleEndDelay={config.cycleEndDelay * 1000}
            cycleCount={this.state.cycleCount}
            expectedGroupSize={this.state.groupSize}
            quality={this.state.quality}
            deletedCount={this.props.deletedCount}
            didFinishCycle={() => this.nextAction()}
            debug={config.debug}
          />
        </GridWrapper>
      </>
    )
  }
}